// THE PRIORITY SCAN'S TAB STRIP.
//
// Lifted out of App.js unchanged. Two reasons, and the second is the one that matters:
//
//   1. App.js is ~50,000 lines, and a control whose POSITION is the whole bug it just
//      had deserves to be somewhere a reader can find it.
//   2. IT COULD NOT BE TESTED OR SCREENSHOTTED WHERE IT WAS. It was a module-private
//      function inside a file that pulls in the entire application, so proving the
//      compact variant renders the way it is supposed to meant either booting the app
//      or hand-building a mockup and calling it the shipped component — which it would
//      not have been. A control that cannot be shown is a control whose next placement
//      bug gets found by a customer again.
//
// The behaviour is byte-identical to what shipped; only its address changed.

import React from "react";

// ── WHERE THE TAB STRIP ACTUALLY HAS TO LIVE ─────────────────────────────────
//
// This rendered as a full-width underlined tab strip at the very top of the Priority
// Scan — above the explainer banner, above the coach card, above the new-admissions
// banner. All three are tall, so by the time a unit manager is looking at the roster
// the tabs have scrolled off the top of the screen and the 24-hour chart check is, in
// the only sense that matters, not there. It was reported as exactly that: "I don't see
// the tab for the 24 hour check anywhere in priority scan."
//
// A control nobody can find is not a smaller version of a control: it is an absent one,
// and the chart check is the screen she opens at 10am. So `compact` renders the same two
// choices as a segmented pill that sits INLINE in the roster toolbar, beside
// "225 current residents · page 1 of 9" — the one row that is always in view, directly
// above the table, and never pushed down by a banner.
//
// Same component and same state either way: two tab strips that could disagree about
// which tab is open is the thing worth avoiding more than the duplication.
export default function ScanTabs({ tab, onTab, compact = false }) {
  const TABS = [
    { key: "RISK", label: "Risk ranking", short: "Risk", hint: "Every resident, ranked by composite risk" },
    { key: "CHART_CHECK", label: "24-hour chart check", short: "24-hour check", hint: "Only the charts that had activity yesterday" },
  ];
  if (compact) {
    return (
      <div
        role="tablist"
        aria-label="Priority Scan view"
        style={{
          display: "inline-flex",
          gap: 2,
          padding: 2,
          background: "var(--ed-line-2)",
          border: "1px solid var(--ed-line)",
          borderRadius: 8,
        }}
      >
        {TABS.map((t) => {
          const on = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              onClick={() => onTab(t.key)}
              title={t.hint}
              aria-selected={on}
              style={{
                padding: "5px 11px",
                background: on ? "var(--ed-canvas)" : "transparent",
                border: "none",
                borderRadius: 6,
                boxShadow: on ? "0 1px 2px rgba(15,23,42,.10)" : "none",
                color: on ? "var(--ed-brand)" : "var(--ed-muted)",
                fontSize: 12.5,
                fontWeight: on ? 800 : 600,
                cursor: "pointer",
                fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              {t.short}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, borderBottom: "1px solid var(--ed-line)" }}>
      {TABS.map((t) => {
        const on = tab === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onTab(t.key)}
            title={t.hint}
            aria-current={on ? "page" : undefined}
            style={{
              padding: "8px 13px",
              marginBottom: -1,
              background: "none",
              border: "none",
              borderBottom: `2px solid ${on ? "var(--ed-brand)" : "transparent"}`,
              color: on ? "var(--ed-brand)" : "var(--ed-muted)",
              fontSize: 14,
              fontWeight: on ? 800 : 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
